import { CartAddEvent, ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @returns {string}
 */
function getCartSectionIds() {
  /** @type {string[]} */
  const ids = [];

  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      ids.push(item.dataset.sectionId);
    }
  });

  return ids.join(',');
}

/**
 * @param {{ id: number, quantity: number }[]} items
 * @param {string} sourceId
 * @param {{ openDrawer?: boolean, source?: string }} [options]
 * @returns {Promise<object | null>}
 */
async function addItemsToCart(items, sourceId, options = {}) {
  const sectionIds = getCartSectionIds();
  const openDrawer = options.openDrawer !== false;
  const source = options.source || 'sleepwhisper-ajax-add';

  const response = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      items,
      ...(sectionIds ? { sections: sectionIds } : {}),
    }),
  });

  const data = await response.json();

  if (data.status) {
    window.alert(data.message || data.description || 'Unable to add this item to your cart.');
    return null;
  }

  const cartResponse = await fetch('/cart.js');
  const cart = await cartResponse.json();

  document.dispatchEvent(
    new CartAddEvent(cart, sourceId, {
      source,
      itemCount: cart.item_count ?? items.reduce((sum, item) => sum + item.quantity, 0),
      sections: data.sections,
    })
  );

  if (openDrawer) {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && 'open' in drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  }

  return cart;
}

/**
 * @param {HTMLFormElement} form
 * @param {HTMLButtonElement | null} button
 */
async function handleAjaxAdd(form, button) {
  const formData = new FormData(form);
  const variantId = Number(formData.get('id'));
  const quantity = Number(formData.get('quantity') || 1);

  if (!variantId) return;

  if (button) button.disabled = true;

  try {
    await addItemsToCart([{ id: variantId, quantity }], form.id || 'sw-ajax-add');

    const modal = form.closest('dialog');
    if (modal instanceof HTMLDialogElement && modal.open) {
      modal.close();
    }
  } catch (error) {
    console.error(error);
  } finally {
    if (button) button.disabled = false;
  }
}

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('[data-sw-ajax-add]')) return;

    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    handleAjaxAdd(form, button instanceof HTMLButtonElement ? button : null);
  },
  true
);

/**
 * @param {HTMLElement} root
 */
function setAddonToButtonMode(root) {
  root.dataset.swAddonMode = 'button';

  const switchWrap = root.querySelector('[data-sw-addon-switch]');
  const atc = root.querySelector('[data-sw-addon-atc]');
  const bundle = root.querySelector('[data-sw-addon-bundle]');

  if (switchWrap instanceof HTMLElement) switchWrap.hidden = true;
  if (atc instanceof HTMLElement) atc.hidden = false;
  if (bundle instanceof HTMLInputElement) {
    bundle.checked = false;
    bundle.tabIndex = -1;
  }

  updateAtcTotals();
}

/**
 * @returns {{ format: string, currency: string }}
 */
function getMoneyConfig() {
  const box = document.querySelector('[data-sw-atc-money-format]');
  const format = box instanceof HTMLElement ? box.dataset.swAtcMoneyFormat || '${{amount}}' : '${{amount}}';
  const currency =
    (box instanceof HTMLElement && box.dataset.swAtcCurrency) || Shopify?.currency?.active || 'USD';
  return { format, currency };
}

/**
 * @returns {number}
 */
function getMainUnitPrice() {
  const priced = document.querySelector('product-form-component [data-sw-atc-unit-price]');
  if (priced instanceof HTMLElement) return Number(priced.dataset.swAtcUnitPrice) || 0;
  return 0;
}

/**
 * @returns {number}
 */
function getMainQuantity() {
  const input = document.querySelector('product-form-component input[name="quantity"]');
  const value = input instanceof HTMLInputElement ? Number(input.value) : 1;
  return Number.isFinite(value) && value > 0 ? value : 1;
}

/**
 * @returns {number}
 */
function getAddonTotal() {
  let total = 0;

  document.querySelectorAll('[data-sw-addon][data-sw-addon-mode="switch"] [data-sw-addon-bundle]:checked').forEach((input) => {
    const root = input.closest('[data-sw-addon]');
    if (!(root instanceof HTMLElement)) return;
    total += Number(root.dataset.swAddonPrice) || 0;
  });

  return total;
}

function updateAtcTotals() {
  const targets = document.querySelectorAll('[data-sw-atc-total]');
  if (!targets.length) return;

  const { format, currency } = getMoneyConfig();
  const total = getMainUnitPrice() * getMainQuantity() + getAddonTotal();
  const formatted = formatMoney(total, format, currency);

  targets.forEach((el) => {
    el.textContent = formatted;
  });
}

/**
 * @returns {{ id: number, quantity: number }[]}
 */
function getCheckedBundleItems() {
  /** @type {{ id: number, quantity: number }[]} */
  const items = [];

  document.querySelectorAll('[data-sw-addon][data-sw-addon-mode="switch"] [data-sw-addon-bundle]:checked').forEach((input) => {
    const root = input.closest('[data-sw-addon]');
    if (!(root instanceof HTMLElement)) return;
    const variantId = Number(root.dataset.swAddonVariantId);
    if (!variantId) return;
    items.push({ id: variantId, quantity: 1 });
  });

  return items;
}

let bundlingAddon = false;

document.addEventListener(CartAddEvent.eventName, async (event) => {
  const detail = /** @type {{ data?: { didError?: boolean, source?: string } }} */ (event).detail;
  if (detail?.data?.didError) return;

  const source = detail?.data?.source;
  if (source !== 'product-form-component') return;

  const extras = getCheckedBundleItems();

  document.querySelectorAll('[data-sw-addon][data-sw-addon-mode="switch"]').forEach((root) => {
    if (root instanceof HTMLElement) setAddonToButtonMode(root);
  });

  if (!extras.length || bundlingAddon) return;

  bundlingAddon = true;
  try {
    await addItemsToCart(extras, 'sw-bundle-addon', {
      openDrawer: false,
      source: 'sleepwhisper-bundle-addon',
    });
  } catch (error) {
    console.error(error);
  } finally {
    bundlingAddon = false;
  }
});

/**
 * @param {HTMLElement} root
 * @param {string} targetId
 */
function openAddonDetails(root, targetId) {
  const dialog = root.querySelector('[data-sw-addon-dialog]');
  const content = root.querySelector('[data-sw-addon-content]');
  if (!(dialog instanceof HTMLDialogElement) || !(content instanceof HTMLElement)) return;

  const template = root.querySelector(`#${CSS.escape(targetId)}`);
  if (!template || !(template instanceof HTMLTemplateElement)) return;

  content.replaceChildren(template.content.cloneNode(true));
  dialog.showModal();
}

/**
 * @param {HTMLElement} root
 */
function initAddon(root) {
  if (root.dataset.swAddonReady === 'true') return;
  root.dataset.swAddonReady = 'true';

  const dialog = root.querySelector('[data-sw-addon-dialog]');
  const content = root.querySelector('[data-sw-addon-content]');
  const zoom = root.querySelector('[data-sw-addon-zoom-dialog]');

  if (dialog instanceof HTMLDialogElement) {
    dialog.addEventListener('click', (event) => {
      const panel = dialog.querySelector('.sw-addon-modal__panel');
      if (event.target === dialog || (panel && !panel.contains(/** @type {Node} */ (event.target)))) {
        dialog.close();
      }
    });

    dialog.addEventListener('close', () => {
      if (content instanceof HTMLElement) content.replaceChildren();
    });
  }

  if (zoom instanceof HTMLDialogElement) {
    zoom.addEventListener('click', (event) => {
      if (event.target === zoom) zoom.close();
    });
  }
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const zoomButton = target.closest('[data-sw-addon-zoom]');
  if (zoomButton instanceof HTMLElement) {
    const root = zoomButton.closest('[data-sw-addon]');
    if (!(root instanceof HTMLElement)) return;
    initAddon(root);
    const zoom = root.querySelector('[data-sw-addon-zoom-dialog]');
    if (zoom instanceof HTMLDialogElement) zoom.showModal();
    return;
  }

  const zoomClose = target.closest('[data-sw-addon-zoom-close]');
  if (zoomClose instanceof HTMLElement) {
    const zoom = zoomClose.closest('[data-sw-addon-zoom-dialog]');
    if (zoom instanceof HTMLDialogElement) zoom.close();
    return;
  }

  const openButton = target.closest('[data-sw-addon-open]');
  if (openButton instanceof HTMLElement) {
    const root = openButton.closest('[data-sw-addon]');
    const targetId = openButton.dataset.addonTarget;
    if (!(root instanceof HTMLElement) || !targetId) return;
    initAddon(root);
    openAddonDetails(root, targetId);
    return;
  }

  const closeButton = target.closest('[data-sw-addon-close]');
  if (closeButton instanceof HTMLElement) {
    const dialog = closeButton.closest('[data-sw-addon-dialog]');
    if (dialog instanceof HTMLDialogElement) dialog.close();
  }
});

document.querySelectorAll('[data-sw-addon]').forEach((root) => {
  if (root instanceof HTMLElement) initAddon(root);
});

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (!target.matches('[data-sw-addon-bundle]')) return;
  updateAtcTotals();
});

document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
  const detail = /** @type {{ detail?: { cartLine?: unknown } }} */ (event).detail;
  if (detail?.cartLine) return;
  updateAtcTotals();
});

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const price = /** @type {{ detail?: { resource?: { price?: number | string } } }} */ (event).detail?.resource
    ?.price;
  if (price != null) {
    document.querySelectorAll('[data-sw-atc-unit-price]').forEach((el) => {
      if (el instanceof HTMLElement) el.dataset.swAtcUnitPrice = String(price);
    });
  }

  requestAnimationFrame(updateAtcTotals);
});

updateAtcTotals();
